import { useCallback } from 'react';
import {
  CAPTAIN_OPTION_1_INCLUDED_IN_PRICE,
  CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
  INQUIRY_COOKIE_EXPIRY_IN_DAYS,
  RECENT_SEARCH_COOKIE,
  SAVED_INQUIRY_KEY,
  SEARCH_INQUIRY_LATEST_KEY,
} from 'src/common/constants';
import useCookie from 'src/common/hooks/useCookie';
import useLocalStorage from 'src/common/hooks/useLocalStorage';
import { SavedInquiry } from 'src/common/types/common/SavedInquiry';
import { formatDate } from 'src/common/utils/dateHelpers';
import {
  constructRecentSearchCookie,
  parseRecentSearchCookieToObject,
  type RecentSearchCookie,
} from 'src/common/utils/recentInquiryCookieData';
import { localStore } from 'src/common/utils/storageWrapper';
import { TripCartFormState } from 'src/listing/types';

const useTripCartPersistence = (): { saveFormToStorage } => {
  const [cookieValue, setCookie] = useCookie(RECENT_SEARCH_COOKIE);
  const [savedInquiry, setSavedInquiry] = useLocalStorage<SavedInquiry>(
    SAVED_INQUIRY_KEY,
    {} as SavedInquiry,
  );
  const storeDataInLocalStorage = useCallback((state: TripCartFormState) => {
    const inquiry: SavedInquiry = {
      ...savedInquiry,
      details: {
        ...savedInquiry.details,
        adults: state.formGuests,
        seniors: 0,
        children: 0,
        infants: 0,
        trip_length: state.formDuration,
        preferred_date1: formatDate(state.formSelectedDates[0]),
      },
      saveInquiryDate: new Date(),
      saveInquiryType: 'inquiry',
    };
    setSavedInquiry(inquiry);
    localStore.setItem(SEARCH_INQUIRY_LATEST_KEY, 'inquiry');
  }, [savedInquiry, setSavedInquiry]);

  const storeDataInCookies = useCallback((state: TripCartFormState) => {
    const initialCookieData = cookieValue ? parseRecentSearchCookieToObject(cookieValue) : {};
    const dates = state.formSelectedDates.map((date) => formatDate(date));
    const captained = state.withCaptain === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
    || state.withCaptain === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN;
    const params: RecentSearchCookie = {
      ...initialCookieData,
      max_price: initialCookieData.max_price ?? null,
      min_price: initialCookieData.min_price ?? null,
      dates,
      captained: captained ? 'true' : 'false',
      captainOptionValue: state.withCaptain,
      capacity: `${state.formGuests}`,
      duration: state.formDuration,
    };
    const parsedData = constructRecentSearchCookie(params);
    setCookie(parsedData, { expires: INQUIRY_COOKIE_EXPIRY_IN_DAYS });
  }, [cookieValue, setCookie]);

  const saveFormToStorage = useCallback((state: TripCartFormState) => {
    storeDataInLocalStorage(state);
    storeDataInCookies(state);
  }, [storeDataInCookies, storeDataInLocalStorage]);

  return { saveFormToStorage };
};

export default useTripCartPersistence;
